import { sgSupabase } from "../../lib/sgSupabaseClient";
import api from "../../utils/api";
import { assertCanUseSG } from "./access";
import { toDevicePayload } from "./schemas";
import type { FuelSensor } from "../../Layouts/admin/components/sections/devices/utils/fuelSensor";

export async function notifyV5DeviceSync(sgDeviceId: string | number) {
  try {
    await api.post("/sg-sync/device-from-sg", { sgDeviceId });
  } catch (err) {
    console.warn("[sgSync] notify V5 device falló:", err);
  }
}

export interface V5DeviceDeleteOpts {
  backupSources?: string[];
  purgeOnly?: boolean;
}

export async function notifyV5DeviceDelete(
  sgDeviceId: string | number,
  opts: V5DeviceDeleteOpts = {},
) {
  try {
    await api.post("/sg-sync/device-from-sg", {
      sgDeviceId,
      delete: true,
      backupSources: opts.backupSources ?? null,
      purgeOnly: !!opts.purgeOnly,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  } catch (err) {
    console.warn("[sgSync] notify V5 device delete falló:", err);
  }
}

// Device de v5 con este IMEI (identifier), de cualquier compañía: el equipo de
// SG no sabe en qué compañía de v5 vive. null si v5 no lo tiene.
export async function fetchV5DeviceByImei(imei: string) {
  try {
    const { data } = await api.get("/deviceByIdentifier", {
      params: { identifier: imei.trim() },
      _skipCompanyScope: true,
    } as never);
    return (data?.data ?? null) as
      | { id: string; company_id?: string; fuel_sensor?: FuelSensor | null }
      | null;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

// _skipCompanyScope es obligatorio: sin él el interceptor de utils/api mete el
// company_id de la sesión en el cuerpo del PUT y putDevice MUEVE el device a
// esa compañía. Sólo se manda fuel_sensor.
export async function saveV5FuelSensor(
  v5DeviceId: string,
  fuel_sensor: FuelSensor | null
) {
  assertCanUseSG();
  await api.put("/device", { fuel_sensor }, {
    params: { id: v5DeviceId },
    _skipCompanyScope: true,
  } as never);
}

export interface SgEquipoLookups {
  statuses: any[];
  conditions: any[];
  brands: any[];
  carImages: any[];
}

export async function fetchEquipoLookups(): Promise<SgEquipoLookups> {
  const [statusRes, conditionRes, brandsRes, carRes] = await Promise.all([
    sgSupabase.from("statuses").select("id, name, name_es").order("id"),
    sgSupabase
      .from("device_conditions")
      .select("id, name, status_id")
      .order("name"),
    sgSupabase
      .from("brands")
      .select("id, brand, model, origin, network")
      .order("brand")
      .order("model"),
    sgSupabase.from("car_images").select("id, name").order("name"),
  ]);
  return {
    statuses: statusRes.data ?? [],
    conditions: conditionRes.data ?? [],
    brands: brandsRes.data ?? [],
    carImages: carRes.data ?? [],
  };
}

export async function fetchDevice(id: string | number) {
  const { data, error } = await sgSupabase
    .from("devices")
    .select("*")
    .eq("id", id)
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function saveDevice({
  id,
  isNew,
  values,
}: {
  id?: string | number;
  isNew: boolean;
  values: any;
}) {
  assertCanUseSG();
  const payload = {
    ...toDevicePayload(values),
    updated_at: new Date().toISOString(),
  };
  if (isNew) {
    const { data, error } = await sgSupabase
      .from("devices")
      .insert(payload)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    await notifyV5DeviceSync(data.id);
    return data.id;
  }
  const { error } = await sgSupabase
    .from("devices")
    .update(payload)
    .eq("id", id);
  if (error) throw new Error(error.message);
  await notifyV5DeviceSync(id!);
  return id;
}

export async function deleteDevice(
  id: string | number,
  opts: V5DeviceDeleteOpts = {},
) {
  assertCanUseSG();
  await notifyV5DeviceDelete(id, opts);
  const { error } = await sgSupabase.from("devices").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

// Sin first_name/last_name: en SG ahí se escribía la plataforma a mano
// («WIALON | ARRENDAMIENTO»); ahora sale calculada de sg-realtime (origins.ts).
export const BARRIDO_SELECT =
  "id, imei, phone, simcard, name, last_time_connected," +
  " users(user_name, user_statuses(name))," +
  " brands(brand, model), statuses(name), device_conditions(name)";

const sanitize = (s: string) => s.replace(/[,()*]/g, " ").trim();

const DEVICE_LIST_SELECT =
  "id, name, uniq_id, imei, phone, last_time_connected," +
  " users(user_name), brands(brand, model)," +
  " statuses(name_es, name), device_conditions(name)";

const DEVICE_SEARCH_FIELDS = [
  "name",
  "uniq_id",
  "imei",
  "simcard",
  "phone",
  "plates",
  "brand",
  "model",
  "niv",
  "ip",
];

const searchOr = (q: string) =>
  DEVICE_SEARCH_FIELDS.map((f) => `${f}.ilike.*${q}*`).join(",");

export async function fetchSgDevices(
  page: number,
  search: string,
  perPage: number
): Promise<{ rows: any[]; total: number }> {
  const from = page * perPage;
  let query = sgSupabase
    .from("devices")
    .select(DEVICE_LIST_SELECT, { count: "exact" })
    .order("last_time_connected", { ascending: false })
    .range(from, from + perPage - 1);
  const q = sanitize(search);
  if (q) query = query.or(searchOr(q));
  const { data, error, count } = await query;
  if (error) throw new Error(error.message);
  return { rows: data ?? [], total: count ?? 0 };
}

export async function fetchAllSgDevices(search: string): Promise<any[]> {
  const BATCH = 1000;
  const all: any[] = [];
  let from = 0;
  for (;;) {
    let query = sgSupabase
      .from("devices")
      .select(DEVICE_LIST_SELECT)
      .order("id", { ascending: true })
      .range(from, from + BATCH - 1);
    const q = sanitize(search);
    if (q) query = query.or(searchOr(q));
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    all.push(...(data ?? []));
    if (!data || data.length < BATCH) break;
    from += BATCH;
  }
  return all;
}

const LINEAS_SELECT =
  "id, imei, phone, simcard, name," +
  " users(user_name, user_statuses(name))," +
  " brands(model), statuses(name)";

export interface SgLineaRow {
  id: number;
  imei: string;
  phone: string;
  simcard: string;
  user_name: string;
  user_status: string;
  alias: string;
  model: string;
  device_status: string;
}

function flattenLinea(d: any): SgLineaRow {
  const u = d.users || {};
  return {
    id: d.id,
    imei: d.imei || "",
    phone: d.phone || "",
    simcard: d.simcard || "",
    user_name: u.user_name || "",
    user_status: u.user_statuses?.name || "",
    alias: d.name || "",
    model: d.brands?.model || "",
    device_status: d.statuses?.name || "",
  };
}

export async function fetchAllLineas(
  search: string,
  onProgress?: (n: number) => void
): Promise<SgLineaRow[]> {
  const BATCH = 1000;
  const all: SgLineaRow[] = [];
  let from = 0;
  for (;;) {
    let query = sgSupabase
      .from("devices")
      .select(LINEAS_SELECT)
      .order("id", { ascending: true })
      .range(from, from + BATCH - 1);
    const q = sanitize(search);
    if (q) query = query.or(searchOr(q));
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    all.push(...(data ?? []).map(flattenLinea));
    onProgress?.(all.length);
    if (!data || data.length < BATCH) break;
    from += BATCH;
  }
  return all.sort(
    (a, b) => a.user_name.localeCompare(b.user_name, "es") || a.id - b.id
  );
}
